import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import { hasPermission, permissionScope } from '@/composables/usePermission'

export default function useProcessList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { getItemsAggregate, updateItem, updateItems } = useLambda()
  const userData = store.state?.userStore?.userData
  const userId = userData.worker_id?.$oid
  const processes = ref([])
  const collection = 'process'
  const processTypeFilter = ref([])
  const companyFilter = computed(() => store.state.filtersStore.companyFilter)
  const idFilter = computed(() => store.state.filtersStore.idFilter)
  const processFilter = computed(() => store.state.filtersStore.processFilter)
  const crimeFilter = computed(() => store.state.filtersStore.crimeFilter)
  const controlFilter = computed(() => store.state.filtersStore.controlFilter)
  const processResponsibleFilter = computed(() => store.state.filtersStore.processResponsibleFilter)
  const locationFilter = ref([])
  const controlResponsibleFilter = ref([])
  const inherentRiskFilter = ref(null)
  const residualRiskFilter = ref(null)

  // Table Handlers
  const emptyValueFormatter = value => value || '-'

  const compactCol = 'process-col-compact'
  const wideCol = 'process-col-wide'
  const areasCol = 'process-col-areas'
  const actionsCol = 'process-col-actions'
  const crimesCol = 'process-col-crimes'
  const nameCol = 'process-col-name'
  const activityCol = 'process-col-activity'
  const idCol = 'process-col-id'

  const tableColumns = computed(() => {
    return [
      { key: 'Actions', label: i18n.t('message.tableHeader.actions'), thClass: actionsCol, tdClass: actionsCol },
      { key: 'code', label: 'ID', formatter: emptyValueFormatter, thClass: idCol, tdClass: idCol },
      { key: 'company', label: i18n.t('label.company'), formatter: emptyValueFormatter, thClass: wideCol, tdClass: wideCol },
      { key: 'areas', label: i18n.t('label.Locations'), formatter: emptyValueFormatter, thClass: areasCol, tdClass: areasCol },
      { key: 'id', label: i18n.t('label.businessActivity'), formatter: emptyValueFormatter, thClass: activityCol, tdClass: activityCol },
      { key: 'name', label: i18n.t('label.riskProcess'), formatter: emptyValueFormatter, thClass: nameCol, tdClass: nameCol },
      { key: 'inherentRisk', label: i18n.t('message.tableHeader.inherentRisk'), thClass: compactCol, tdClass: compactCol },
      { key: 'residualRisk', label: i18n.t('message.tableHeader.residualRisk'), thClass: compactCol, tdClass: compactCol },
      { key: 'crimes', label: i18n.t('label.crimes'), thClass: crimesCol, tdClass: crimesCol },
      { key: 'controlsData', label: i18n.t('message.tableHeader.controlsAndResponsibles'), thClass: compactCol, tdClass: compactCol },
    ]
  })

  const totalProcesses = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('code')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchProcesses = async () => {
    isLoading.value = true

    try {
      const filter = {
        client_id: oid(userData.client.$oid),
        deleted: { $ne: true }
      }

      if (companyFilter.value.length > 0) {
        filter.company = { $in: companyFilter.value.map(e => oid(e)) }
      }

      if (idFilter.value.length > 0) {
        filter.id = { $in: idFilter.value.map(e => oid(e)) }
      }

      if (processFilter.value.length > 0) {
        filter._id = { $in: processFilter.value.map(e => oid(e)) }
      }

      if (locationFilter.value.length > 0) {
        filter.areas = { $in: locationFilter.value.map(e => oid(e)) }
      }

      if (crimeFilter.value.length > 0) {
        filter.crimes = { $in: crimeFilter.value.map(e => oid(e)) }
      }

      if (controlFilter.value.length > 0) {
        filter.controls = { $in: controlFilter.value.map(e => oid(e)) }
      }

      if (processResponsibleFilter.value.length > 0) {
        filter.$or = [
          { responsibles: { $in: processResponsibleFilter.value.map(e => oid(e)) } },
          { leadResponsibles: { $in: processResponsibleFilter.value.map(e => oid(e)) } }
        ]
      }

      const andConditions = []

      // Capa 2: "Ver propio" restringe a los procesos donde el usuario es responsable,
      // responsable líder o evaluador — mismo criterio que viewButtonEnabled más abajo,
      // pero acá contra los campos crudos (ObjectId) antes del $lookup/stringify.
      if (permissionScope('risks', 'view') === 'own') {
        andConditions.push({
          $or: [
            { responsibles: oid(userId) },
            { leadResponsibles: oid(userId) },
            { evaluator: oid(userId) },
          ],
        })
      }

      // Filter by control responsible needs to be done after lookup
      const postLookupConditions = []

      if (controlResponsibleFilter.value.length > 0) {
        postLookupConditions.push({
          controlsData: {
            $elemMatch: {
              responsible: { $in: controlResponsibleFilter.value }
            }
          }
        })
      }

      if (processTypeFilter.value.length) {
        andConditions.push({
          $or: processTypeFilter.value.map(codePrefix => ({
            $expr: {
              $regexMatch: {
                input: "$code",
                regex: `^${codePrefix}`
              }
            }
          }))
        });
      }

      if (searchQuery.value) {
        andConditions.push({
          $or: [
            { name: { $regex: searchQuery.value, $options: 'i' } },
            { code: { $regex: searchQuery.value, $options: 'i' } }
          ]
        });
      }

      if (inherentRiskFilter.value) {
        andConditions.push({
          inherentRisk: inherentRiskFilter.value
        });
      }

      if (residualRiskFilter.value) {
        andConditions.push({
          residualRisk: residualRiskFilter.value
        });
      }

      if (andConditions.length) {
        filter.$and = andConditions
      }
    
      const pipeline = [
        { $match: filter },
        {
          $lookup: {
            from: "crime",
            localField: "crimes",
            foreignField: "_id",
            as: "crimesData",
            pipeline: [ { $project: { _id: 0, name: 1 } } ]
          }
        },
        {
          $lookup: {
            from: "control",
            localField: "controls",
            foreignField: "_id",
            as: "controlsData",
            pipeline: [
              { $project: { name: 1, responsible: 1, responsible_id: 1 } },
              { $lookup: { from: 'worker', localField: 'responsible_id', foreignField: '_id', as: 'responsibleWorkerData', pipeline: [ { $project: { name: 1 } } ] } },
              { $addFields: { responsible: { $ifNull: [ { $arrayElemAt: ["$responsibleWorkerData.name", 0] }, "$responsible" ] } } },
              { $project: { name: 1, responsible: 1 } }
            ]
          }
        },
        // This next stage sorts the controlsData array in the same order as the controls array
        {
          $addFields: {
            controlsData: {
              $map: {
                input: "$controls",
                as: "controlId",
                in: { $arrayElemAt: [ "$controlsData", { $indexOfArray: ["$controlsData._id", "$$controlId"] } ] }
              }
            }
          }
        },
        {
          $lookup: {
            from: "company",
            localField: "company",
            foreignField: "_id",
            as: "company",
            pipeline: [ { $project: { name: 1 } } ]
          }
        },
        {
          $lookup: {
            from: "id",
            localField: "id",
            foreignField: "_id",
            as: "id",
            pipeline: [ { $project: { name: 1 } } ]
          }
        },
        {
          $lookup: {
            from: "location",
            localField: "areas",
            foreignField: "_id",
            as: "areasData",
            pipeline: [ { $project: { location: 1 } } ]
          }
        },
        { $addFields: { responsibles: { $map: { input: "$responsibles", as: "id", in: { $toString: "$$id" } } }, leadResponsibles: { $map: { input: "$leadResponsibles", as: "id", in: { $toString: "$$id" } } }, evaluator: { $toString: "$evaluator" }, company: { $arrayElemAt: ["$company.name", 0] }, id: { $arrayElemAt: ["$id.name", 0] }, areas: { $map: { input: "$areasData", as: "area", in: "$$area.location" } } } },
      ]

      // Add post-lookup filter if responsible filter is active
      if (postLookupConditions.length > 0) {
        pipeline.push({ $match: { $and: postLookupConditions } })
      }

      pipeline.push(
        { $project: { name: 1, code: 1, crimesData: 1, controlsData: 1, responsibles: 1, leadResponsibles: 1, evaluator: 1, residualRisk: 1, inherentRisk: 1, company: 1, id: 1, areas: 1 } },
        { $facet: {
            count: [ { $count: 'totalItems' } ],
            pagination: [
              { $sort: { [sortBy.value]: isSortDirDesc.value ? -1 : 1, _id: isSortDirDesc.value ? -1 : 1 } },
              { $skip: (currentPage.value * perPage.value) - perPage.value || 0 },
              { $limit: perPage.value || 10 }
            ]
          }
        }
      )
    
      const items = await getItemsAggregate({ collection, pipeline })

      items[0]?.pagination?.forEach(e => {
        e.crimes = e.crimesData?.map(c => c.name).join(" / ") || ''
        e.areas = e.areas?.join(" / ") || ''
        e.viewButtonEnabled = e.responsibles?.includes(userId) || e.leadResponsibles?.includes(userId) || e.evaluator === userId
        e.residualRiskComputed = computed(() => e.residualRisk ? i18n.t(`residualRisk.${e.residualRisk}`) : '')
        e.inherentRiskComputed = computed(() => e.inherentRisk ? i18n.t(`residualRisk.${e.inherentRisk}`) : '')
      })

      processes.value = items[0]?.pagination || []
      totalProcesses.value = items[0]?.count[0]?.totalItems || 0
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.process_list_fetch_error'))
    } finally {
      isLoading.value = false
    }
  }

  const deleteProcess = async (id) => {
    if (!hasPermission('risks', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    try {
      const query = { _id: oid(id) }
      const payload = { deleted: true }
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      showSuccessMessage(i18n.t('message.process_remove'))
      fetchProcesses()

      // Delete confirmation plannings associated to this process
      await updateItems({ collection: 'confirmation_planning', query: { process: oid(id) }, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.process_remove_error'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (processes.value.length ? 1 : 0)
    const to = processes.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalProcesses.value })
  })

  watch([currentPage, perPage], () => {
    fetchProcesses()
  })

  watch([searchQuery, sortBy, isSortDirDesc, companyFilter, idFilter, processFilter, crimeFilter, controlFilter, processResponsibleFilter], () => {
    currentPage.value = 1
    fetchProcesses()
  })

  const updateFilter = (data) => {
    processTypeFilter.value = data.processTypeFilter
    locationFilter.value = data.locationFilter || []
    controlResponsibleFilter.value = data.controlResponsibleFilter || []
    inherentRiskFilter.value = data.inherentRiskFilter || null
    residualRiskFilter.value = data.residualRiskFilter || null

    fetchProcesses()
  }

  return {
    fetchProcesses,
    tableColumns,
    perPage,
    currentPage,
    totalProcesses,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    processes,
    deleteProcess,
    isLoading,
    perPageOptions,
    updateFilter,
  }
}
